import Splitting from 'splitting';
import gsap from 'gsap';

const DEFAULT_OPTIONS = {
  maskRevealText: {
    duration: 1,
    revealDirection: 'up',
    delay: 0,
  },
  maskReveal: {
    delay: 0,
    yStart: -200,
    yEnd: 0,
    ease: 'power1',
    duration: 1,
  },
};

const buildElementWrapper = () => {
  const wrapperEl = document.createElement('span');
  wrapperEl.classList.add('element-mask');
  return wrapperEl;
};

/**
 * Based off the revealDirection, determine whether the
 */
const getRevealDirectionCoefficient = (configObject) => {
  const cfg = configObject;

  return cfg.revealDirection === 'up' ? 1 : -1;
};

const maskRevealText = (element, options = {}, callback) => {
  if (!element) return;

  options = {
    ...DEFAULT_OPTIONS.maskRevealText,
    ...options,
  };

  const [{ words }] = new Splitting({
    target: element,
    by: 'words',
  });

  const revealDirectionCoefficient = getRevealDirectionCoefficient(options);
  const { revealDirection } = options;
  const numberOfWords = words.length;

  words.forEach((word, index) => {
    const { textContent } = word;
    const iteration = index + 1;

    // Wrap text content in a span. This is the element we'll translate.
    // Also creates the mask that will obscure the element either on the top
    // or bottom
    word.innerHTML = `
      <span class="word__text">${textContent}</span>
      <span role='presentation' class="word__mask"></span>
    `;
    word.style.overflow = 'hidden';

    const wrapper = word.querySelector('.word__text');
    const mask = word.querySelector('.word__mask');

    // Determine which css property to adjust based off the revealDirection
    const cssPropertyMap = {
      down: 'top',
      up: 'bottom',
    };

    // Position the mask with some offset. The offset is an arbitrary value
    // that appears to hold up across typography styles pretty well. Depending on
    // this is not ideal, but it works for now
    const positionOffset = '10px';
    mask.style[cssPropertyMap[revealDirection]] = `calc(-100% - ${positionOffset})`; // i.e.: bottom: calc(-100% - 10px)

    // Used to decrement a z-index value for both the text and mask of an
    // individual word. This decrementing value ensures proceeding words are
    // not concealed by a previous word's mask during the animation
    const zIndexOffset = revealDirection === 'down' ? iteration : ((numberOfWords + 1) - iteration);
    const wordZIndex = zIndexOffset * -2;
    const maskZIndex = zIndexOffset * -1;

    word.style.zIndex = wordZIndex;
    wrapper.style.zIndex = maskZIndex;

    word.style.visibility = 'visible';

    const toAnimation = {
      yPercent: 0,
      duration: options.duration,
      delay: options.delay,
    };

    if (callback) {
      toAnimation.onComplete = callback;
    }

    // Animate
    gsap.fromTo(wrapper, {
      yPercent: 100 * revealDirectionCoefficient,
      onComplete() {
        element.style.opacity = 1;
      },
    }, toAnimation);
  });
};

const maskReveal = (element, options = {}) => {
  const elementChildren = element?.children;

  if (!elementChildren) {
    return;
  }

  options = {
    ...DEFAULT_OPTIONS.maskReveal,
    ...options,
  };

  if (options.wrapInMask) {
    const wrapperEl = buildElementWrapper();
    element.append(wrapperEl);
    elementChildren.forEach(child => wrapperEl.append(child));
  } else {
    if (element.classList.contains('invisible')) {
      element.classList.remove('invisible');
    }
    gsap.fromTo(element, {
      y: options.yStart * -1,
    }, {
      y: options.yEnd,
      duration: options.duration,
      delay: options.delay,
      ease: options.ease,
      onComplete: () => {
        element.parentElement.style.overflow = 'visible';
      },
    });
  }
};

export {
  maskRevealText,
  maskReveal,
};
